// Analytics 段共用的小组件：KPI 卡片 / 时间范围切换 / 图表 / 数字 / 货币格式化。
//
// 图表层：之前是纯 SVG 手撕（无 tooltip / axis / responsive），现在统一走
// recharts 3.x。primitives 的 props 保持原样 —— callers 无需改动。
// 多列布局由新的 ChartGrid 包装器承担，让相关图表在宽屏并排，不再每张占
// 一整行（参照 admin platform dashboard 的密度）。

import React from 'react';
import { Select as BaseUiSelect } from '../../ui/select.jsx';
import {
  ResponsiveContainer,
  BarChart as RcBarChart,
  Bar,
  LineChart as RcLineChart,
  Line,
  AreaChart as RcAreaChart,
  Area,
  PieChart as RcPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  LabelList,
  CartesianGrid,
  Tooltip as RcTooltip,
} from 'recharts';

// ── 格式化 ───────────────────────────────────────────────────────────────
export const fmtNum = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (v >= 1_000) return (v / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(Math.round(v));
};

export const fmtUsd = (n) => {
  const v = Number(n) || 0;
  if (v === 0) return '$0';
  if (Math.abs(v) < 0.01) return '<$0.01';
  return '$' + v.toFixed(v < 100 ? 4 : 2).replace(/\.?0+$/, '');
};

// H-fix: for transaction-log tables (settlement / individual usage records)
// where every row matters and "<$0.01" masking would make every row look
// identical. Always shows 4 decimals; never masks. Use fmtUsd for KPI cards
// where signal-to-noise favors masking.
export const fmtUsdExact = (n) => {
  const v = Number(n) || 0;
  if (v === 0) return '$0';
  // 4 decimals for sub-dollar, 2 for ≥$1 — same precision threshold as
  // fmtUsd but never collapses to '<$0.01'.
  return '$' + v.toFixed(Math.abs(v) < 1 ? 4 : 2);
};

// 极小金额下标零计数法。per-call 毛收入/花费常是几分之一分(如 $0.00000200),
// fmtUsd 会塌成 "<$0.01"、fmtUsdExact 的 4 位小数会变 "$0.0000" —— 两者都把
// 数量级抹掉。这里沿用 CoinGecko/Binance 习惯:前导 0 的个数写成下标,再跟有效位。
//   1.234      → $1.23
//   0.0234     → $0.0234
//   0.00000200 → $0.0₅2     (下标 5 = 小数点后前导 0 的个数)
//   0.00000767 → $0.0₅767
const TINY_SUB = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
const tinySub = (z) => String(z).split('').map((c) => TINY_SUB[+c] ?? c).join('');
export const fmtTinyUsd = (n, sign = false) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  if (num === 0) return '$0';
  const abs = Math.abs(num);
  const s = num < 0 ? '-' : sign ? '+' : '';
  if (abs >= 1) return s + '$' + abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (abs >= 0.0001) {
    // up to 4 decimals, strip trailing zeros but keep at least 2 (so $0.05 stays $0.05)
    let str = abs.toFixed(4).replace(/0+$/, '');
    if ((str.split('.')[1] || '').length < 2) str = abs.toFixed(2);
    return s + '$' + str;
  }
  // < 0.0001 → 下标零计数:下标 = 小数点后前导 0 的个数,后跟前几位有效数字
  const lz = -Math.floor(Math.log10(abs)) - 1;
  const sig = (abs * Math.pow(10, lz + 1)).toFixed(2).replace('.', '').replace(/0+$/, '') || '0';
  return s + '$0.0' + tinySub(lz) + sig;
};

export const fmtPct = (n, digits = 1) => {
  if (n == null || isNaN(n)) return '—';
  return (Number(n) * 100).toFixed(digits) + '%';
};

// D3 — 字节数格式化（10.5 KB / 12.3 MB / 4.2 GB）
export const fmtBytes = (n) => {
  const v = Number(n) || 0;
  if (v === 0) return '0 B';
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / 1024 / 1024).toFixed(1)} MB`;
  return `${(v / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

export const fmtRelDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / (24 * 3600_000));
  if (days < 1) return 'today';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

// ── KPI 卡片 ─────────────────────────────────────────────────────────────
// dim=true 时数值色降级到 fg-3，避免在「全 0 状态」下还高对比闪人眼。
// trend={current, previous, higherIsBetter?} 时显示环比箭头：
//   ↑12.3% 绿（涨且 higherIsBetter）/ 红（涨但 lowerIsBetter，如延迟、错误率）
//   ↓5.0%  反之
//   prev=0 但 current>0 → 'NEW' 标记；都是 0 → 不显示。
// `size="lg"` bumps the type one step for pages whose surrounding text sits at
// the 13px console baseline rather than the denser analytics scale.
export const KpiCard = ({ label, value, hint, accent, dim, trend, size }) => {
  const lg = size === 'lg';
  let trendNode = null;
  if (trend && typeof trend.current === 'number') {
    const cur = trend.current;
    const prev = trend.previous;
    const higherBetter = trend.higherIsBetter !== false; // 默认 true
    if (prev === 0 && cur > 0) {
      trendNode = (
        <span style={{ fontSize: 10, color: '#22a06b', marginLeft: 6 }} className="mono">NEW</span>
      );
    } else if (prev > 0) {
      const pct = ((cur - prev) / prev) * 100;
      const up = pct > 0;
      const flat = Math.abs(pct) < 0.5;
      const good = flat ? null : (up === higherBetter);
      const color = flat ? 'var(--fg-3)' : good ? '#22a06b' : '#c0392b';
      const arrow = flat ? '·' : up ? '↑' : '↓';
      trendNode = (
        <span
          style={{ fontSize: 10, color, marginLeft: 6 }}
          className="mono"
          title={`前一窗口: ${prev}`}
        >
          {arrow}{Math.abs(pct).toFixed(1)}%
        </span>
      );
    }
  }
  return (
    <div
      style={{
        padding: '14px 16px',
        border: '1px solid var(--bd-1)',
        borderRadius: 8,
        background: 'var(--bg-0)',
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: lg ? 12 : 11, color: 'var(--fg-3)', marginBottom: 6 }}>{label}</div>
      <div
        style={{
          fontSize: lg ? 24 : 22,
          fontWeight: 500,
          color: dim ? 'var(--fg-3)' : (accent || 'var(--fg-0)'),
          letterSpacing: '-0.01em',
          lineHeight: 1.1,
          display: 'flex',
          alignItems: 'baseline',
        }}
      >
        <span>{value}</span>
        {trendNode}
      </div>
      {hint && (
        <div style={{ fontSize: lg ? 12 : 11, color: 'var(--fg-3)', marginTop: 4 }}>{hint}</div>
      )}
    </div>
  );
};

export const KpiGrid = ({ children }) => (
  <div
    style={{
      display: 'grid',
      // 140 instead of 160 so the 9-card Traffic grid (after D3 added
      // 总带宽/平均请求/平均响应) fits on common ~1400px viewports
      // without orphaning the last card on its own row. Labels stay
      // readable down to 140px; smaller would start truncating numbers.
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: 14,
      marginBottom: 20,
    }}
  >
    {children}
  </div>
);

// ── 时间范围切换 ────────────────────────────────────────────────────────
//
// 支持两种 value：
//   • preset 字符串：'1d' / '7d' / '30d' / '90d'
//   • 'custom'：跟随 customRange 的 from / to
//
// 用法（pages 端）：
//   const [range, setRange] = useState('30d');
//   const [customRange, setCustomRange] = useState({ from: '', to: '' });
//   const { from, to, daysApprox } = rangeToBounds(range, customRange);
//   // ...把 from / to 传给后端 endpoint（ISO），daysApprox 给只吃 days 的 endpoint

export const RANGE_OPTIONS = [
  { v: '1d', l: '24h', days: 1 },
  { v: '7d', l: '7 天', days: 7 },
  { v: '30d', l: '30 天', days: 30 },
  { v: '90d', l: '90 天', days: 90 },
  { v: 'custom', l: '自定义', days: null }, // 自定义时 days 来自 customRange
];

export const RangePicker = ({ value, onChange, customRange, onCustomRangeChange }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div
        style={{
          display: 'inline-flex',
          border: '1px solid var(--bd-1)',
          borderRadius: 6,
          overflow: 'hidden',
          background: 'var(--bg-1)',
        }}
      >
        {RANGE_OPTIONS.map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => {
              onChange(opt.v);
              if (opt.v === 'custom') setOpen(true);
              else setOpen(false);
            }}
            style={{
              padding: '6px 12px',
              border: 'none',
              background: value === opt.v ? 'var(--bg-3)' : 'transparent',
              color: value === opt.v ? 'var(--fg-0)' : 'var(--fg-3)',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            {opt.l}
            {opt.v === 'custom' && value === 'custom' && customRange?.from && customRange?.to && (
              <span style={{ marginLeft: 4, color: 'var(--fg-2)', fontSize: 10 }} className="mono">
                {customRange.from.slice(5)}–{customRange.to.slice(5)}
              </span>
            )}
          </button>
        ))}
      </div>

      {value === 'custom' && open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 4px)',
            background: 'var(--bg-0)',
            border: '1px solid var(--bd-1)',
            borderRadius: 6,
            padding: 12,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            zIndex: 20,
            minWidth: 280,
          }}
        >
          <div style={{ fontSize: 11, color: 'var(--fg-3)', marginBottom: 8 }}>
            自定义时间范围（UTC）
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input
              type="date"
              value={customRange?.from || ''}
              onChange={(e) => onCustomRangeChange?.({ ...customRange, from: e.target.value })}
              style={{
                flex: 1,
                padding: '5px 8px',
                background: 'var(--bg-1)',
                border: '1px solid var(--bd-1)',
                borderRadius: 4,
                color: 'var(--fg-0)',
                fontSize: 11,
              }}
            />
            <span style={{ color: 'var(--fg-3)', fontSize: 11 }}>→</span>
            <input
              type="date"
              value={customRange?.to || ''}
              onChange={(e) => onCustomRangeChange?.({ ...customRange, to: e.target.value })}
              style={{
                flex: 1,
                padding: '5px 8px',
                background: 'var(--bg-1)',
                border: '1px solid var(--bd-1)',
                borderRadius: 4,
                color: 'var(--fg-0)',
                fontSize: 11,
              }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={!customRange?.from || !customRange?.to}
              style={{
                padding: '4px 12px',
                background:
                  customRange?.from && customRange?.to ? 'var(--fg-0)' : 'var(--bg-1)',
                color:
                  customRange?.from && customRange?.to ? 'var(--bg-0)' : 'var(--fg-3)',
                border: 'none',
                borderRadius: 4,
                fontSize: 11,
                cursor:
                  customRange?.from && customRange?.to ? 'pointer' : 'not-allowed',
              }}
            >应用</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Section 容器 ────────────────────────────────────────────────────────
//
// `compact` 模式：放在 ChartGrid 里时把 marginBottom / padding 收窄，让网格
// 间隙自己承担间距，避免双倍空白。
// compact = ChartGrid 里并排的图表 cell，保留轻量边框卡片让相邻 cell 区分开
// （admin 参考里图表也是带框的）；非 compact = 整宽内容 section，改为扁平 band
// 与背景融合，靠 hairline 跟相邻 section 分隔，不再套大框（#12 的诉求）。
export const Section = ({ title, action, children, compact = false }) => {
  const header = (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: compact ? 8 : 12,
        gap: 8,
      }}
    >
      <h3 style={{ margin: 0, fontSize: 12, fontWeight: 500, color: 'var(--fg-1)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {title}
      </h3>
      {action}
    </header>
  );

  if (compact) {
    return (
      <section
        style={{
          border: '1px solid var(--bd-1)',
          borderRadius: 8,
          padding: '12px 14px',
          background: 'var(--bg-0)',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        {header}
        <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
      </section>
    );
  }

  return (
    <section
      className="panel-flat"
      style={{ padding: '16px 0', display: 'flex', flexDirection: 'column', minWidth: 0 }}
    >
      {header}
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </section>
  );
};

// ── ChartGrid: 多列图表网格 ─────────────────────────────────────────────
//
// 把若干个 <Section compact> 包进来，宽屏下自动按 minWidth 拼成 2-4 列；
// 窄屏自动折回单列。参照 admin platform dashboard 的密度，让单页能塞下更
// 多图表而不必每张占满一整行。
//
// minWidth: 单个图表 cell 的最小宽度（决定列数）。默认 360 让常规图在
// 1440 viewport 下显示 3-4 列；窄一点的图（比如 P95/error rate 组合）可
// 以传 280 让宽屏多挤一列。
export const ChartGrid = ({ children, minWidth = 360, gap = 12, className }) => (
  <div
    className={className}
    style={{
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))`,
      gap,
      marginBottom: 16,
    }}
  >
    {children}
  </div>
);

// ── 简单柱状图 (时序数据) ───────────────────────────────────────────────
// onBarClick(point, index) — 若传入则柱子可点击（下钻到对应时段日志等）
// ── 柱状图 ──────────────────────────────────────────────────────────────
//
// data: [{ ts | date, [valueKey]: number }]
// onBarClick(d, i): 点击单根柱子时的钩子（比如下钻到该时段的日志）
//
// 之前是裸 SVG <rect> + hover-title。现在走 recharts，自带 hover tooltip /
// cursor / 自适应轴。空态保留原有 EmptyBarChart 提示。
export const BarChart = ({
  data,
  valueKey = 'calls',
  height = 120,
  emptyHint,
  emptyZeroHint,
  onBarClick,
}) => {
  if (!data || data.length === 0) {
    return <EmptyBarChart height={height} hint={emptyHint} />;
  }
  const max = Math.max(...data.map((d) => Number(d[valueKey]) || 0), 0);
  if (max === 0) {
    return (
      <EmptyBarChart
        height={height}
        hint={emptyZeroHint || '该时间窗口内调用量为 0'}
        subHint="切换时间范围或等待新调用"
      />
    );
  }
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <RcBarChart
          data={data}
          margin={{ top: 6, right: 8, left: 4, bottom: 0 }}
        >
          <CartesianGrid stroke="var(--line)" strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey={data[0]?.ts != null ? 'ts' : 'date'}
            stroke="var(--fg-4)"
            tick={{ fontSize: 10, fill: 'var(--fg-4)' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => String(v).slice(5, 16)}
            minTickGap={20}
          />
          <YAxis
            stroke="var(--fg-4)"
            tick={{ fontSize: 10, fill: 'var(--fg-4)' }}
            axisLine={false}
            tickLine={false}
            width={36}
            allowDecimals={false}
            tickFormatter={fmtNum}
          />
          <RcTooltip
            cursor={{ fill: 'var(--bg-2)', opacity: 0.5 }}
            contentStyle={chartTooltipStyle}
            labelStyle={chartTooltipLabelStyle}
            itemStyle={{ color: 'var(--fg-1)' }}
            formatter={(v) => fmtNum(v)}
          />
          <Bar
            dataKey={valueKey}
            fill="var(--c-mint)"
            radius={[2, 2, 0, 0]}
            cursor={onBarClick ? 'pointer' : undefined}
            onClick={onBarClick ? (entry, i) => onBarClick(entry, i) : undefined}
          />
        </RcBarChart>
      </ResponsiveContainer>
    </div>
  );
};

// 共享的 tooltip 样式 —— 暗色卡片，跟其它 surface 保持视觉一致
const chartTooltipStyle = {
  background: 'var(--bg-1)',
  border: '1px solid var(--line)',
  borderRadius: 6,
  padding: '6px 10px',
  fontSize: 11,
  color: 'var(--fg-1)',
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
};
const chartTooltipLabelStyle = {
  color: 'var(--fg-3)',
  fontSize: 10,
  marginBottom: 2,
};

// ── 折线图 (mean / P50 / P95 多线) ──────────────────────────────────────
//
// data: [{ ts, [key1], [key2], ... }]
// series: [{ key, label, color }]
// null/undefined 值留空（hour 粒度下 P50/P95 缺失时不连线）—— recharts 的
// `connectNulls={false}` 默认行为正好如此。
export const LineChart = ({ data, series, height = 160, unit = 'ms', showPoints = true }) => {
  if (!data || data.length === 0) {
    return <EmptyBarChart height={height} hint="首次调用后这里会显示真实数据" />;
  }
  const allValues = [];
  series.forEach((s) => {
    data.forEach((d) => {
      const v = d[s.key];
      if (v != null && Number.isFinite(v)) allValues.push(v);
    });
  });
  if (allValues.length === 0) {
    return (
      <EmptyBarChart
        height={height}
        hint="该时间窗口内没有可用的延迟数据"
        subHint="切换时间范围或粒度"
      />
    );
  }
  return (
    <div style={{ position: 'relative', width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <RcLineChart
          data={data}
          margin={{ top: 6, right: 8, left: 4, bottom: 0 }}
        >
          <CartesianGrid stroke="var(--line)" strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="ts"
            stroke="var(--fg-4)"
            tick={{ fontSize: 10, fill: 'var(--fg-4)' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => String(v).slice(5, 16)}
            minTickGap={20}
          />
          <YAxis
            stroke="var(--fg-4)"
            tick={{ fontSize: 10, fill: 'var(--fg-4)' }}
            axisLine={false}
            tickLine={false}
            width={40}
            tickFormatter={(v) => `${fmtNum(v)}${unit ? '' : ''}`}
          />
          <RcTooltip
            cursor={{ stroke: 'var(--fg-3)', strokeDasharray: '3 3' }}
            contentStyle={chartTooltipStyle}
            labelStyle={chartTooltipLabelStyle}
            itemStyle={{ color: 'var(--fg-1)' }}
            formatter={(v, name) => [v == null ? '—' : `${v}${unit || ''}`, name]}
          />
          {series.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={s.color}
              strokeWidth={1.6}
              // 稀疏数据（长窗口里只有几天有流量）下，no-data 日是 null。
              // connectNulls 把缺口桥接成一条连续趋势线（不再断断续续），
              // 同时画出每个真实数据点的小圆点，缺口被桥接但真实点位仍可辨，
              // 不误导。activeDot 在 hover 时放大。
              dot={showPoints ? { r: 2, strokeWidth: 0, fill: s.color } : false}
              activeDot={{ r: 3, stroke: 'var(--bg-1)', strokeWidth: 1.5, fill: s.color }}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </RcLineChart>
      </ResponsiveContainer>
      {/* 紧凑 legend，固定在右上角；recharts 的内置 Legend 会单独占一行，
          这里手撕一行密度更高 */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 4,
          display: 'flex',
          gap: 10,
          fontSize: 10,
          color: 'var(--fg-3)',
          pointerEvents: 'none',
        }}
      >
        {series.map((s) => (
          <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ display: 'inline-block', width: 10, height: 2, background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
};

// ── 堆叠柱状图 (成功 / 失败 / 消费者侧) ──────────────────────────────────
// data: [{ ts, successCalls, failedCalls, consumerErrorCalls }]
//
// 成功用 mint 品牌色，失败(provider 侧 UPSTREAM)用 rose 危险色。
// consumerErrorCalls = 消费者侧(USER 余额 / REQUEST 参数·模型名)，不计入服务质量，
// 用 fg-4 中性灰**灰显**在最底部（口径见后端 docs/provider-metrics-口径规范.md）。
// show: 'all' | 'success' | 'failed' —— 全量堆叠(含消费者侧) / 只画成功 / 只画失败
// onBarClick(point,i): 点击某根柱子(下钻到该时段日志,与调用量时序一致)
export const StackedBarChart = ({ data, height = 160, onBarClick, show = 'all' }) => {
  if (!data || data.length === 0) {
    return <EmptyBarChart height={height} />;
  }
  const showSuccess = show !== 'failed';
  const showFailed = show !== 'success';
  const showConsumer = show === 'all'; // 消费者侧仅在全量堆叠时灰显
  const max = Math.max(
    ...data.map(
      (d) =>
        (d.successCalls || 0) +
        (d.failedCalls || 0) +
        (showConsumer ? d.consumerErrorCalls || 0 : 0),
    ),
    0,
  );
  if (max === 0) {
    return (
      <EmptyBarChart
        height={height}
        hint="该时间窗口内没有调用"
        subHint="成功 / 失败堆叠"
      />
    );
  }
  const totalSuccess = data.reduce((s, d) => s + (d.successCalls || 0), 0);
  const totalFailed = data.reduce((s, d) => s + (d.failedCalls || 0), 0);
  const totalConsumer = data.reduce(
    (s, d) => s + (d.consumerErrorCalls || 0),
    0,
  );

  return (
    <div style={{ position: 'relative', width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <RcBarChart data={data} margin={{ top: 6, right: 8, left: 4, bottom: 0 }}>
          <CartesianGrid stroke="var(--line)" strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="ts"
            stroke="var(--fg-4)"
            tick={{ fontSize: 10, fill: 'var(--fg-4)' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => String(v).slice(5, 16)}
            minTickGap={20}
          />
          <YAxis
            stroke="var(--fg-4)"
            tick={{ fontSize: 10, fill: 'var(--fg-4)' }}
            axisLine={false}
            tickLine={false}
            width={36}
            allowDecimals={false}
            tickFormatter={fmtNum}
          />
          <RcTooltip
            cursor={{ fill: 'var(--bg-2)', opacity: 0.5 }}
            contentStyle={chartTooltipStyle}
            labelStyle={chartTooltipLabelStyle}
            itemStyle={{ color: 'var(--fg-1)' }}
            formatter={(v, name) => [
              fmtNum(v),
              name === 'successCalls'
                ? '成功'
                : name === 'failedCalls'
                  ? '失败'
                  : '消费者侧',
            ]}
          />
          {/* 堆叠自下而上：消费者侧(灰) → 失败(rose) → 成功(mint)。消费者侧灰显在最底，
              视觉上"服务侧成功+失败 + 底下一层与我无关的消费者噪声"。show 控制只画其一。 */}
          {showConsumer && (
            <Bar
              dataKey="consumerErrorCalls" stackId="calls" fill="var(--fg-4)" radius={[0, 0, 0, 0]}
              cursor={onBarClick ? 'pointer' : undefined}
              onClick={onBarClick ? (entry, i) => onBarClick(entry, i) : undefined}
            />
          )}
          {showFailed && (
            <Bar
              dataKey="failedCalls" stackId="calls" fill="var(--c-rose)" radius={[0, 0, 0, 0]}
              cursor={onBarClick ? 'pointer' : undefined}
              onClick={onBarClick ? (entry, i) => onBarClick(entry, i) : undefined}
            />
          )}
          {showSuccess && (
            <Bar
              dataKey="successCalls" stackId="calls" fill="var(--c-mint)" radius={[2, 2, 0, 0]}
              cursor={onBarClick ? 'pointer' : undefined}
              onClick={onBarClick ? (entry, i) => onBarClick(entry, i) : undefined}
            />
          )}
        </RcBarChart>
      </ResponsiveContainer>
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 4,
          display: 'flex',
          gap: 10,
          fontSize: 10,
          color: 'var(--fg-3)',
          pointerEvents: 'none',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ display: 'inline-block', width: 10, height: 8, background: 'var(--c-mint)' }} />
          成功 {fmtNum(totalSuccess)}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ display: 'inline-block', width: 10, height: 8, background: 'var(--c-rose)' }} />
          失败 {fmtNum(totalFailed)}
        </span>
        {showConsumer && totalConsumer > 0 && (
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            title="消费者侧错误（余额不足 / 参数·模型名错），不计入服务质量"
          >
            <span style={{ display: 'inline-block', width: 10, height: 8, background: 'var(--fg-4)' }} />
            消费者侧 {fmtNum(totalConsumer)}
          </span>
        )}
      </div>
    </div>
  );
};

// ── Sparkline (7 days, 用于 user 行) ────────────────────────────────────
//
// 极简的 inline 趋势线 —— 用 recharts AreaChart 带一点 fill，比纯线条更有
// 视觉量感，但跟 console 页 StatCard 的 sparkline 风格保持一致。
export const Sparkline = ({ values, width = 80, height = 22 }) => {
  if (!values || values.length === 0) return null;
  const data = values.map((v, i) => ({ i, v: Number(v) || 0 }));
  return (
    <div style={{ width, height, display: 'inline-block' }}>
      <ResponsiveContainer width="100%" height="100%">
        <RcAreaChart data={data} margin={{ top: 1, right: 0, left: 0, bottom: 1 }}>
          <defs>
            <linearGradient id="sparklineFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--fg-1)" stopOpacity={0.35} />
              <stop offset="100%" stopColor="var(--fg-1)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="v"
            stroke="var(--fg-1)"
            strokeWidth={1.2}
            fill="url(#sparklineFill)"
            isAnimationActive={false}
          />
        </RcAreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── 横向堆叠条（延迟构成 / 占比拆分） ────────────────────────────────────
//
// segments: [{ label, value, color, hint?, unit? }]
// 单条占满宽度，每段按 value 比例分；hover 触发 recharts tooltip 同时看
// 数值+百分比；下方留一行紧凑 legend 汇总每段绝对值 / 样本数。
//
// recharts 不擅长单点 stacked bar，但 layout="vertical" + 一行数据 +
// 每段一个 <Bar> 是 idiomatic 用法；LabelList 处理 ≥10% 段的内联 % 标签。
export const HorizontalStack = ({ segments, height = 22 }) => {
  const total = segments.reduce((s, x) => s + (Number(x.value) || 0), 0);
  if (total === 0) {
    return (
      <div style={{ padding: 12, color: 'var(--fg-3)', fontSize: 11, textAlign: 'center' }}>
        无可用 timing 数据
      </div>
    );
  }
  // 单数据点：每段的 label 作为 dataKey，value 作为该 key 的数值。
  // recharts 会按 stackId 把它们堆成一条横向柱。
  const dataPoint = { _bucket: 'breakdown' };
  for (const s of segments) dataPoint[s.label] = Number(s.value) || 0;

  return (
    <div>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <RcBarChart
            data={[dataPoint]}
            layout="vertical"
            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
            barCategoryGap={0}
          >
            <XAxis type="number" hide domain={[0, total]} />
            <YAxis type="category" dataKey="_bucket" hide />
            <RcTooltip
              cursor={false}
              contentStyle={chartTooltipStyle}
              labelStyle={{ display: 'none' }}
              itemStyle={{ color: 'var(--fg-1)', padding: '1px 0' }}
              formatter={(v, name) => {
                const unit = segments.find((s) => s.label === name)?.unit || '';
                const pct = total > 0 ? ((Number(v) / total) * 100).toFixed(1) : '0';
                return [`${v}${unit} (${pct}%)`, name];
              }}
            />
            {segments.map((s, i) => (
              <Bar
                key={s.label}
                dataKey={s.label}
                stackId="breakdown"
                fill={s.color}
                isAnimationActive={false}
                // 圆角只给两头，中间段保持直角，看起来像一根连续条
                radius={
                  segments.length === 1
                    ? [3, 3, 3, 3]
                    : i === 0
                      ? [3, 0, 0, 3]
                      : i === segments.length - 1
                        ? [0, 3, 3, 0]
                        : [0, 0, 0, 0]
                }
              >
                {/* 段宽 ≥10% 才内嵌 % 标签，避免小段被文字撑爆 */}
                <LabelList
                  dataKey={s.label}
                  position="center"
                  fill="rgba(255,255,255,0.95)"
                  style={{ fontSize: 10, fontWeight: 500 }}
                  formatter={(v) => {
                    const pct = total > 0 ? (Number(v) / total) * 100 : 0;
                    return pct >= 10 ? `${Math.round(pct)}%` : '';
                  }}
                />
              </Bar>
            ))}
          </RcBarChart>
        </ResponsiveContainer>
      </div>
      {/* 紧凑 legend：标记 + 段名 + 数值 + 样本数 */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 14,
          marginTop: 10,
          fontSize: 11,
        }}
      >
        {segments.map((s, i) => {
          const v = Number(s.value) || 0;
          return (
            <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  background: s.color,
                  borderRadius: 2,
                }}
              />
              <span style={{ color: 'var(--fg-1)' }}>{s.label}</span>
              <span className="mono" style={{ color: 'var(--fg-3)' }}>
                {v > 0 ? `${v}${s.unit || ''}` : '—'}
                {s.hint && <span style={{ marginLeft: 4 }}>· {s.hint}</span>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── 饼图 (状态码分布) ──────────────────────────────────────────────────
//
// 走 recharts PieChart，自带 hover-highlight slice + tooltip。保持 donut
// 形状（innerRadius>0），让中心可以放一个总数标签时不会被遮挡，同时视觉
// 上比实心 pie 更现代。
export const PieSlices = ({ data, size = 140 }) => {
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0);
  if (total === 0) {
    return (
      <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fg-3)', fontSize: 11 }}>
        无数据
      </div>
    );
  }
  return (
    <div style={{ width: size, height: size }}>
      <ResponsiveContainer width="100%" height="100%">
        <RcPieChart>
          <RcTooltip
            contentStyle={chartTooltipStyle}
            labelStyle={chartTooltipLabelStyle}
            itemStyle={{ color: 'var(--fg-1)' }}
            formatter={(v, name) => [`${fmtNum(v)} (${((v / total) * 100).toFixed(1)}%)`, name]}
          />
          <Pie
            data={data}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius={size * 0.32}
            outerRadius={size * 0.48}
            paddingAngle={1}
            stroke="var(--bg-0)"
            strokeWidth={1}
            isAnimationActive={false}
          >
            {data.map((d, i) => (
              <Cell key={i} fill={d.color} />
            ))}
          </Pie>
        </RcPieChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── CSV 导出 ───────────────────────────────────────────────────────────
//
// 纯客户端：把当前已加载的行转 CSV → 触发浏览器下载。完全不动后端。
// columns: [{ key, label, transform? }]
//   transform 可选，用于格式化（如 ISO date → readable string、cost → fixed digits）
// 没有 transform 时默认 row[key]，null/undefined 输出空字符串。
//
// 注意：值里的 "," 和 "\n" 会用双引号包裹；双引号本身按 RFC 4180 用 "" 转义。
const csvEscape = (v) => {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export const downloadCsv = (rows, columns, filename) => {
  const header = columns.map((c) => csvEscape(c.label ?? c.key)).join(',');
  const body = rows
    .map((row) =>
      columns
        .map((c) => csvEscape(c.transform ? c.transform(row[c.key], row) : row[c.key]))
        .join(','),
    )
    .join('\n');
  // BOM 让 Excel 识别 utf-8 中文
  const csv = '﻿' + header + '\n' + body;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

// 通用导出按钮，放在 Section action 槽里。disabled 时灰显（如 rows 为空）。
export const ExportCsvButton = ({ rows, columns, filename, disabled }) => (
  <button
    type="button"
    onClick={() => downloadCsv(rows, columns, filename)}
    disabled={disabled || !rows?.length}
    title={!rows?.length ? '当前没有可导出的行' : `导出 ${rows.length} 行到 CSV`}
    style={{
      padding: '4px 10px',
      border: '1px solid var(--bd-1)',
      background: 'transparent',
      color: rows?.length ? 'var(--fg-2)' : 'var(--fg-3)',
      borderRadius: 4,
      fontSize: 10,
      cursor: rows?.length ? 'pointer' : 'not-allowed',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
    }}
  >
    ⬇ CSV
  </button>
);

// ── 专业表格原子 ────────────────────────────────────────────────────────
//
// Phase 2 v2：服务商面板的表格不再只是「列数据」，要支持筛选 / 排序 /
// 翻页这三件套。下面这组小组件是 Traffic / Users / Revenue 共用的积木：
//
//   <FilterBar>       一行 wrap 的筛选项容器
//   <PillGroup>       一组互斥选项（如 All / 2xx / 4xx / 5xx）
//   <Select>          原生 select 的暗主题包装
//   <SearchInput>     带左侧 magnifier 占位符的搜索框
//   <SortHeader>      可点击的列头，带 ↑↓ 指示符
//   <StatusCodePill>  HTTP 状态码的语义化色块
//   <MethodPill>      HTTP 方法的低饱和色块
//   <SettlementPill>  PENDING / SETTLED 色块
//   <CountryCell>     国家旗 emoji + ISO code
//   <DataTable>       带 sticky thead / hover row 的表格壳
//
// 这些只关心展示；筛选 / 排序的状态由调用方维护，把变化通过 onChange 推给
// 父组件 → 父组件触发 fetch。这样切换 / 翻页时不会丢已有 UI 状态。

// 横向 wrap 的筛选项容器。直接子代之间插一根细竖线把组分开 ——
// 之前是纯 gap，多组并排时筛选块边界不明，看不出"端点 / 状态 / 方法 /
// 调用方"是 4 个独立维度。Filter 子组件们已经各自带 label，竖线只是
// 视觉锚点，所以只做 1px 虚线 / 极低对比度。
export const FilterBar = ({ children }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
        padding: '10px 0',
        marginBottom: 8,
      }}
    >
      {items.map((child, i) => (
        <React.Fragment key={i}>
          {child}
          {i < items.length - 1 && (
            <span
              aria-hidden
              style={{
                display: 'inline-block',
                width: 1,
                height: 18,
                background: 'var(--line)',
                opacity: 0.6,
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// 一组互斥选项 (e.g. All / 2xx / 4xx / 5xx 的胶囊按钮组)
export const PillGroup = ({ value, options, onChange, label }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    {label && (
      <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{label}</span>
    )}
    <div
      style={{
        display: 'inline-flex',
        border: '1px solid var(--bd-1)',
        borderRadius: 6,
        overflow: 'hidden',
        background: 'var(--bg-1)',
      }}
    >
      {options.map((o) => (
        <button
          key={o.v ?? '__all__'}
          type="button"
          onClick={() => onChange(o.v)}
          style={{
            padding: '5px 10px',
            border: 'none',
            background: value === o.v ? 'var(--bg-3)' : 'transparent',
            color: value === o.v ? 'var(--fg-0)' : 'var(--fg-3)',
            fontSize: 11,
            cursor: 'pointer',
          }}
        >
          {o.l}
        </button>
      ))}
    </div>
  </div>
);

// 原生 select 的暗主题包装
// 用 base-ui 的 Select 替换原生 <select>（原生在暗色 UI 下是 OS 默认样式,跟全站
// 不一致 —— #20）。options 沿用本段 {v,l} 形状,内部映射成 base-ui 的 {value,label};
// 选 placeholder（value=''）回调 null,保持原有筛选契约。trigger 收紧到筛选条尺寸。
export const Select = ({ value, onChange, options, label, placeholder = '全部', width = 160 }) => {
  const opts = [
    { value: '', label: placeholder },
    ...options.map((o) => ({ value: o.v, label: o.l })),
  ];
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {label && <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{label}</span>}
      <BaseUiSelect
        value={value || ''}
        onChange={(v) => onChange(v || null)}
        options={opts}
        placeholder={placeholder}
        width={width}
        triggerStyle={{ height: 28, fontSize: 11, padding: '0 8px 0 10px', borderColor: 'var(--bd-1)' }}
      />
    </div>
  );
};

export const SearchInput = ({ value, onChange, placeholder = '搜索…', width = 200 }) => (
  <div style={{ position: 'relative', width, display: 'inline-block' }}>
    <span
      style={{
        position: 'absolute',
        left: 8,
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: 11,
        color: 'var(--fg-3)',
        pointerEvents: 'none',
      }}
    >
      🔍
    </span>
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '6px 8px 6px 28px',
        background: 'var(--bg-1)',
        border: '1px solid var(--bd-1)',
        borderRadius: 6,
        color: 'var(--fg-0)',
        fontSize: 11,
        boxSizing: 'border-box',
      }}
    />
  </div>
);

// 列头：sortBy={current key} dir={'asc'|'desc'} onSort(key) — 调用方维护
// 状态并在 fetch 里把它转成 orderBy 参数。
export const SortHeader = ({ children, sortKey, currentSort, currentDir, onSort, align = 'left', width }) => {
  const active = currentSort === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      style={{
        padding: '6px 8px',
        textAlign: align,
        fontWeight: 400,
        color: active ? 'var(--fg-0)' : 'var(--fg-3)',
        cursor: 'pointer',
        userSelect: 'none',
        width,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {children}
        <span style={{ fontSize: 9, opacity: active ? 1 : 0.3 }}>
          {active ? (currentDir === 'asc' ? '▲' : '▼') : '▾'}
        </span>
      </span>
    </th>
  );
};

// 状态码胶囊：色与饼图 legend 对齐
const STATUS_CODE_COLORS = {
  '2xx': '#22a06b',
  '3xx': '#5b8def',
  '4xx': '#e08e0b',
  '5xx': '#c0392b',
};
export const StatusCodePill = ({ code }) => {
  const c = Number(code) || 0;
  const bucket = c >= 500 ? '5xx' : c >= 400 ? '4xx' : c >= 300 ? '3xx' : c >= 200 ? '2xx' : null;
  const color = bucket ? STATUS_CODE_COLORS[bucket] : 'var(--fg-3)';
  return (
    <span
      className="mono"
      style={{
        display: 'inline-block',
        padding: '1px 6px',
        background: color + '22',
        color,
        borderRadius: 3,
        fontSize: 11,
        fontWeight: 500,
        minWidth: 30,
        textAlign: 'center',
      }}
    >
      {code}
    </span>
  );
};

const METHOD_COLORS = {
  GET: '#5b8def',
  POST: '#22a06b',
  PUT: '#e08e0b',
  PATCH: '#9b59b6',
  DELETE: '#c0392b',
};
export const MethodPill = ({ method }) => {
  const m = (method || 'GET').toUpperCase();
  const color = METHOD_COLORS[m] || 'var(--fg-3)';
  return (
    <span
      className="mono"
      style={{
        display: 'inline-block',
        padding: '1px 5px',
        background: color + '22',
        color,
        borderRadius: 3,
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: '0.02em',
      }}
    >
      {m}
    </span>
  );
};

export const SettlementPill = ({ status }) => {
  const palette = status === 'SETTLED'
    ? { bg: '#22a06b22', fg: '#22a06b', label: 'SETTLED' }
    : status === 'PENDING'
    ? { bg: '#e08e0b22', fg: '#e08e0b', label: 'PENDING' }
    : { bg: 'var(--bg-1)', fg: 'var(--fg-3)', label: status || '—' };
  return (
    <span
      style={{
        padding: '2px 6px',
        fontSize: 10,
        background: palette.bg,
        color: palette.fg,
        borderRadius: 4,
        fontWeight: 500,
      }}
    >
      {palette.label}
    </span>
  );
};

// 把 ISO 2-letter 转 flag emoji（regional indicator letter codepoint）。
// 不靠任何依赖 / 不需要图片资源。null/未知 → 显示 🌐 占位。
export const countryFlag = (iso) => {
  if (!iso || typeof iso !== 'string' || iso.length !== 2) return '🌐';
  const cc = iso.toUpperCase();
  const A = 0x1f1e6 - 'A'.charCodeAt(0);
  return String.fromCodePoint(cc.charCodeAt(0) + A, cc.charCodeAt(1) + A);
};

export const CountryCell = ({ iso }) => (
  <span className="mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    <span style={{ fontSize: 13 }}>{countryFlag(iso)}</span>
    <span>{iso || '—'}</span>
  </span>
);

// 表格壳：sticky thead，rows 带 hover 与点击态。
export const DataTable = ({ children, dense = false }) => (
  <div style={{ overflowX: 'auto' }}>
    <table
      style={{
        width: '100%',
        fontSize: 12,
        borderCollapse: 'collapse',
        ...(dense && { lineHeight: 1.4 }),
      }}
    >
      {children}
    </table>
  </div>
);

// 「加载更多 / 全部加载完毕」分页脚
export const TableFooter = ({ hasMore, loading, onLoadMore, totalLoaded, totalLabel = '已加载' }) => (
  <div
    style={{
      padding: '12px 0 4px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: 11,
      color: 'var(--fg-3)',
    }}
  >
    <span>
      {totalLabel} {totalLoaded} 条{!hasMore && totalLoaded > 0 ? '（已到底）' : ''}
    </span>
    {hasMore && (
      <button
        type="button"
        onClick={onLoadMore}
        disabled={loading}
        style={{
          padding: '5px 14px',
          background: 'var(--bg-1)',
          border: '1px solid var(--bd-1)',
          color: 'var(--fg-1)',
          borderRadius: 6,
          fontSize: 11,
          cursor: loading ? 'wait' : 'pointer',
        }}
      >
        {loading ? '加载中…' : '加载更多 ↓'}
      </button>
    )}
  </div>
);

// ── 通用空状态 ──────────────────────────────────────────────────────────
export const EmptyHint = ({ children }) => (
  <div style={{ padding: 16, textAlign: 'center', color: 'var(--fg-3)', fontSize: 12 }}>
    {children}
  </div>
);

// ── 富空态：替代「无数据」纯文字 ────────────────────────────────────────
// 设计意图：
//   原本 EmptyHint 只渲染一行灰字，整个 Section 看上去像「半成品 / 没加载完」。
//   富空态用一组淡淡的虚线骨架占住版面，再叠一行引导文案，让用户立刻能读出
//   "组件是活的、只是还没数据" —— 视觉密度跟有数据时基本一致。
//
// 三种形状（对应 3 类图表）：
//   - <EmptyBarChart />  柱状图位置
//   - <EmptyPie />       状态码饼图位置
//   - <EmptyTable cols={N} rows={3} />  endpoint / 调用方 / 日志表位置
// 全部带一致的引导文案。

const GUIDANCE_TEXT = '首次调用后这里会显示真实数据';

// 共享：在骨架上方居中叠一行引导文字。
const OverlayHint = ({ text = GUIDANCE_TEXT, subText }) => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
      gap: 4,
    }}
  >
    <div style={{ fontSize: 12, color: 'var(--fg-2)' }}>{text}</div>
    {subText && (
      <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>{subText}</div>
    )}
  </div>
);

// 柱状图骨架：14 根高度 sin 波动的虚线 bar。
export const EmptyBarChart = ({ height = 160, hint, subHint }) => {
  const bars = 14;
  // 用 sin + 偏移生成「自然但确定」的形状，重渲染不抖动。
  const heights = Array.from({ length: bars }, (_, i) =>
    0.35 + 0.5 * Math.abs(Math.sin((i + 1) * 0.7)),
  );
  const barW = 100 / bars;
  return (
    <div style={{ position: 'relative', height }}>
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height, display: 'block' }}
      >
        {heights.map((h, i) => {
          const barH = h * (height - 16);
          return (
            <rect
              key={i}
              x={i * barW + barW * 0.18}
              y={height - barH - 4}
              width={barW * 0.64}
              height={barH}
              fill="none"
              stroke="var(--bd-2)"
              strokeWidth="0.4"
              strokeDasharray="1.4 1.6"
              rx="1"
            />
          );
        })}
        {/* 基线 */}
        <line
          x1="0"
          y1={height - 4}
          x2="100"
          y2={height - 4}
          stroke="var(--bd-1)"
          strokeWidth="0.5"
        />
      </svg>
      <OverlayHint text={hint || GUIDANCE_TEXT} subText={subHint} />
    </div>
  );
};

// 饼图骨架：两层虚线同心圆。
export const EmptyPie = ({ size = 120, hint }) => (
  <div style={{ position: 'relative', width: size, height: size }}>
    <svg viewBox={`0 0 ${size} ${size}`} style={{ width: size, height: size }}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={size / 2 - 4}
        fill="none"
        stroke="var(--bd-2)"
        strokeWidth="1"
        strokeDasharray="3 4"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={size / 2 - 14}
        fill="none"
        stroke="var(--bd-1)"
        strokeWidth="0.6"
        strokeDasharray="2 3"
      />
    </svg>
    {hint && (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          color: 'var(--fg-3)',
          pointerEvents: 'none',
          textAlign: 'center',
          padding: '0 8px',
        }}
      >
        {hint}
      </div>
    )}
  </div>
);

// 表格骨架：N 行 × M 列虚线条。
export const EmptyTable = ({
  cols = 4,
  rows = 3,
  hint,
  subHint,
}) => (
  <div style={{ position: 'relative' }}>
    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
      <tbody>
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r} style={{ borderTop: '1px solid var(--bd-1)' }}>
            {Array.from({ length: cols }).map((__, c) => (
              <td key={c} style={{ padding: '10px 8px' }}>
                <div
                  style={{
                    height: 8,
                    background: 'transparent',
                    border: '1px dashed var(--bd-2)',
                    borderRadius: 2,
                    width: c === 0 ? '70%' : c === cols - 1 ? '40%' : '55%',
                    opacity: 0.6,
                  }}
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    <OverlayHint text={hint || GUIDANCE_TEXT} subText={subHint} />
  </div>
);

// ── 通用 loading ───────────────────────────────────────────────────────
export const LoadingHint = () => (
  <div style={{ padding: 16, textAlign: 'center', color: 'var(--fg-3)', fontSize: 12 }}>
    Loading…
  </div>
);

// 把 range key 转换为 days
export const rangeToDays = (range) => RANGE_OPTIONS.find((r) => r.v === range)?.days || 30;

// 把 preset 或 custom range 都归一成 { from, to, daysApprox } 三元组。
// custom 但 from/to 未填全时，退化到 30d（让 UI 不会因为空字符串炸掉）。
// 返回 ISO 字符串，方便直接传给后端 query。
export const rangeToBounds = (range, customRange) => {
  const now = new Date();
  if (range === 'custom' && customRange?.from && customRange?.to) {
    const from = new Date(customRange.from + 'T00:00:00.000Z');
    const to = new Date(customRange.to + 'T23:59:59.999Z');
    const daysApprox = Math.max(
      1,
      Math.ceil((to.getTime() - from.getTime()) / (24 * 3600_000)),
    );
    return { from: from.toISOString(), to: to.toISOString(), daysApprox };
  }
  const days = rangeToDays(range);
  const from = new Date(now.getTime() - days * 24 * 3600_000);
  return { from: from.toISOString(), to: now.toISOString(), daysApprox: days };
};
